// src/components/BottomNav.jsx

import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, Bookmark, Newspaper, Settings, Users } from 'lucide-react';

// Navigation items with their corresponding paths
const navItems = [
  { key: 'dashboard', icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
  { key: 'community', icon: Users, label: 'Community', path: '/dashboard/community' },
  { key: 'watchlist', icon: Bookmark, label: 'Watchlist', path: '/dashboard/watchlist' },
  { key: 'news', icon: Newspaper, label: 'News', path: '/dashboard/news' },
  { key: 'settings', icon: Settings, label: 'Settings', path: '/dashboard/settings' },
];

/**
 * BottomNav component provides a navigation bar for mobile/tablet views.
 * It highlights the currently active page and handles navigation between sections.
 * Styled for both Light and Dark modes to match the Dashboard aesthetic.
 */
const BottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isNavigating, setIsNavigating] = useState(false);
  
  // Determine the active page based on the current path
  const getActivePage = () => {
    const currentPath = location.pathname;
    
    // Check if we're on a profile page
    if (currentPath.includes('/dashboard/profile/')) {
      return 'community'; // Highlight community button when on profile page
    }
    
    // Find the matching nav item based on the current path
    const activeItem = navItems.find(item => {
      if (item.key === 'dashboard') {
        // For dashboard, check if the path is exactly '/dashboard'
        return currentPath === item.path;
      }
      // For other items, check if the current path includes the item's path
      return currentPath.includes(item.path);
    });
    
    return activeItem ? activeItem.key : 'dashboard';
  };
  
  const activePage = getActivePage();

  // Handle navigation with delay for dashboard button
  const handleNavigation = (path, key) => {
    if (isNavigating) return; // Prevent multiple clicks while navigating
    
    // Add delay for dashboard button to avoid potential issues
    if (key === 'dashboard') {
      setIsNavigating(true);
      setTimeout(() => {
        navigate(path);
        setIsNavigating(false);
      }, 300); // 300ms delay
    } else {
      navigate(path);
    }
  };

  return (
    // Updated styling: 
    // - bg-white / dark:bg-gray-900 for background
    // - border-gray-200 / dark:border-gray-800 for separator
    // - Added fixed positioning and safe area insets for mobile
    <nav className="fixed bottom-0 left-0 right-0 h-20 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 z-40 backdrop-blur-lg md:hidden">
      <div className="flex justify-around items-center h-full max-w-2xl mx-auto px-1">
        {navItems.map(({ key, icon: Icon, label, path }) => (
          <button
            key={key}
            onClick={() => handleNavigation(path, key)}
            disabled={isNavigating && key === 'dashboard'}
            className={`flex flex-col items-center justify-center flex-1 h-full transition-colors duration-200 rounded-lg px-1 py-1 ${
              activePage === key 
                ? 'text-blue-600 dark:text-blue-400' 
                : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'
            } ${isNavigating && key === 'dashboard' ? 'opacity-50 cursor-not-allowed' : ''}`}
            aria-label={`Navigate to ${label}`}
          >
            <Icon size={22} />
            <span className="sr-only">{label}</span>
          </button>
        ))}
      </div>
    </nav>
  );
};

export default BottomNav;
